import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import {
  CheckmarkCircle20Px,
  CloseCircle20Px,
  useToast
} from "@qubit/autoparts";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectIsShortSideBin,
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import { useReconfigureBinCompartmentsMutation } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetBinConfigurationsQuery } from "~/redux/warehouse/bin.hooks";

export function BinReconfigurationModal({
  binNumber,
  open,
  onConfirm,
  onCancel,
  onClose
}: {
  binNumber: number;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const {
    data: availableBinConfigurations,
    error: availableBinConfigurationsError
  } = useGetBinConfigurationsQuery();

  const { errorToast, successToast } = useToast();
  const { t } = useTranslation();
  const trackedModalName = useMixpanelModalName("Select Bin Configuration");

  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const [reconfigureBinCompartments, { isLoading }] =
    useReconfigureBinCompartmentsMutation();

  const [selectedConfiguration, setSelectedConfiguration] = useState<
    null | number
  >(null);

  if (availableBinConfigurationsError)
    errorToast(getMessageFromRtkError(availableBinConfigurationsError), {
      trackingLocation: trackedModalName
    });

  const handleReconfigureCompartments = async () => {
    if (!selectedConfiguration) return;
    const selectedBinConfiguration = availableBinConfigurations?.find(
      (bc) => bc.configurationType === selectedConfiguration
    );

    if (!selectedWorkstation || !autostoreGridId || !selectedBinConfiguration) {
      return;
    }
    try {
      await reconfigureBinCompartments({
        autostoreGridId,
        binNumber,
        configurationId: selectedBinConfiguration.configurationId,
        workstationId: selectedWorkstation.id
      }).unwrap();

      successToast(
        `Successfully reconfigured bin to have ${selectedBinConfiguration.numberOfCompartments} compartments`
      );
      onClose();
      onConfirm();
    } catch (err) {
      errorToast(getMessageFromRtkError(err), {
        trackingLocation: trackedModalName
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        mixpanelTrackModalDismiss(trackedModalName, reason, {
          "Bin Number": binNumber
        });
        onClose();
      }}
      maxWidth="xl"
    >
      <DialogTitle>{t("misconfigured bin")} </DialogTitle>
      <DialogContent>
        <Stack direction="column" gap={2} mt={2} alignItems="center">
          <Typography variant="h5" mt={2}>
            {t("select bin configuration")}
          </Typography>
          <Stack direction="row" gap={1}>
            {availableBinConfigurations?.map((conf) => (
              <Fragment key={conf.configurationId}>
                <Box
                  aria-label={
                    selectedConfiguration === conf.configurationType
                      ? "selected-bin"
                      : "unselected-bin"
                  }
                  sx={{
                    padding: 1,
                    borderRadius: "0.5em",
                    border: `0.625em solid`,
                    borderColor:
                      selectedConfiguration === conf.configurationType
                        ? "primary.main"
                        : "white"
                  }}
                >
                  <AutostoreBin
                    state={"Port Closed"}
                    pickQuantity={0}
                    pickCompartment={null}
                    numberOfRows={conf.horizontalCompartmentCount}
                    numberOfColumns={conf.verticalCompartmentCount}
                    hideBinId
                    onBinClick={() => {
                      setSelectedConfiguration(conf.configurationType);
                      mixpanelTrack({
                        type: "Button Click",
                        label: "Select Bin Configuration",
                        trackedPageName: trackedModalName,
                        eventProperties: {
                          "Bin Number": binNumber,
                          "Configuration Type": conf.configurationType
                        }
                      });
                    }}
                    isShortSideBin={isShortSideBin}
                  />
                </Box>
              </Fragment>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ mb: 2 }}>
        <Stack justifyContent="center" direction="row" width="100%" gap={1}>
          <Button
            variant="subtle"
            size="large"
            startIcon={<CloseCircle20Px />}
            onClick={() => {
              onCancel();
              onClose();
              mixpanelTrack({
                type: "Button Click",
                label: "Cancel",
                trackedPageName: trackedModalName,
                eventProperties: {
                  "Bin Number": binNumber
                }
              });
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<CheckmarkCircle20Px fill="white" />}
            disabled={!selectedConfiguration || isLoading}
            onClick={async () => {
              await handleReconfigureCompartments();
              mixpanelTrack({
                type: "Button Click",
                label: "Confirm",
                trackedPageName: trackedModalName,
                eventProperties: {
                  "Bin Number": binNumber,
                  "Configuration Type": selectedConfiguration
                }
              });
            }}
          >
            {t("confirm")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
